import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { ErrorBoundary } from './app/ErrorBoundary'
import { SessionProvider } from './app/SessionProvider'
import { Splash } from './app/Splash'
import { PageTransition } from './app/PageTransition'
import { RequireAnonymous, RequireAuth } from './app/routes'
import { TabShell } from './app/TabShell'
import { ToastProvider } from './components/ui/Toast'
import { copyTradingPath } from './lib/tradeRoutes'

// Route-level splitting: a phone on a slow connection should not download the
// trading terminal to read its balance.
const SignIn = lazy(() => import('./screens/auth/SignIn'))
const SignUp = lazy(() => import('./screens/auth/SignUp'))
const VerifyEmail = lazy(() => import('./screens/auth/VerifyEmail'))
const ForgotPassword = lazy(() => import('./screens/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./screens/auth/ResetPassword'))

const Home = lazy(() => import('./screens/Home'))
const Markets = lazy(() => import('./screens/Markets'))
const Referrals = lazy(() => import('./screens/referrals/Referrals'))
const TradeHub = lazy(() => import('./screens/TradeHub'))
const Trade = lazy(() => import('./screens/Trade'))
const Chart = lazy(() => import('./screens/Chart'))

const Wallet = lazy(() => import('./screens/wallet/Wallet'))
const Deposit = lazy(() => import('./screens/wallet/Deposit'))
const Withdraw = lazy(() => import('./screens/wallet/Withdraw'))
const Transfer = lazy(() => import('./screens/wallet/Transfer'))
const History = lazy(() => import('./screens/wallet/History'))
const Withdrawals = lazy(() => import('./screens/wallet/Withdrawals'))

const Profile = lazy(() => import('./screens/profile/Profile'))
const Kyc = lazy(() => import('./screens/profile/Kyc'))
const Security = lazy(() => import('./screens/profile/Security'))
const Sessions = lazy(() => import('./screens/profile/Sessions'))
const ThemeScreen = lazy(() => import('./screens/profile/Theme'))
const NotificationSettings = lazy(() => import('./screens/NotificationSettings'))
const AddressBook = lazy(() => import('./screens/wallet/AddressBook'))
const Language = lazy(() => import('./screens/profile/Language'))
const Notifications = lazy(() => import('./screens/Notifications'))
const NotFound = lazy(() => import('./screens/NotFound'))

/**
 * Signal codes are entered in the terminal's Copy trading tab since the
 * 28 September call; before that they had their own tab (`/signals`) and,
 * earlier still, lived under `/copy`. Those addresses stay in bookmarks, push
 * notifications, the bot's messages and the channel's posts, so each of them
 * lands on the tab — and `?code=` still opens that signal's terms at once.
 */
function MovedToCopyTrading(): JSX.Element {
  const location = useLocation()
  const code = new URLSearchParams(location.search).get('code')
  return <Navigate to={copyTradingPath(code)} replace />
}

export function App(): JSX.Element {
  return (
    <SessionProvider>
      <ToastProvider>
      {/* Below this line a throw during render costs one screen and a reload
          button; above it, it would cost the whole app. */}
      <ErrorBoundary>
        <Suspense fallback={<Splash />}>
          <Routes>
            <Route
              path="/signin"
              element={
                <RequireAnonymous>
                  <SignIn />
                </RequireAnonymous>
              }
            />
            <Route
              path="/signup"
              element={
                <RequireAnonymous>
                  <SignUp />
                </RequireAnonymous>
              }
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/copy/signals" element={<MovedToCopyTrading />} />
            <Route path="/copy" element={<MovedToCopyTrading />} />
            <Route path="/copy/*" element={<MovedToCopyTrading />} />

            <Route
              element={
                <RequireAuth>
                  <TabShell />
                </RequireAuth>
              }
            >
              <Route path="/" element={<Home />} />
              <Route path="/markets" element={<Markets />} />
              <Route path="/signals" element={<MovedToCopyTrading />} />
              <Route path="/trade" element={<TradeHub />} />
              <Route path="/trade/terminal" element={<Trade />} />
              <Route path="/wallet" element={<Wallet />} />
            </Route>

            {/* Screens one level below a tab: pushed in from the right, popped
                back out to the left, with no tab bar underneath. */}
            <Route
              element={
                <RequireAuth>
                  <PageTransition>
                    <Outlet />
                  </PageTransition>
                </RequireAuth>
              }
            >
              {/* The chart is a pushed screen (v2 Chart frame): back chevron,
                  the pinned Buy / Sell pair where the tab bar would be. */}
              <Route path="/trade/chart" element={<Chart />} />
              <Route path="/wallet/deposit" element={<Deposit />} />
              <Route path="/wallet/withdraw" element={<Withdraw />} />
              <Route path="/wallet/transfer" element={<Transfer />} />
              <Route path="/wallet/history" element={<History />} />
              <Route path="/wallet/withdrawals" element={<Withdrawals />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/profile/kyc" element={<Kyc />} />
              <Route path="/profile/security" element={<Security />} />
              <Route path="/profile/sessions" element={<Sessions />} />
              <Route path="/profile/theme" element={<ThemeScreen />} />
              <Route path="/wallet/address-book" element={<AddressBook />} />
              <Route path="/profile/language" element={<Language />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/notifications/settings" element={<NotificationSettings />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      </ToastProvider>
    </SessionProvider>
  )
}
